import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Grid, MenuItem, Select, TextField } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import SelectProject from '../Component/SelectProject';
import useUpdateDatabaseRecordStore, { getUpdateDatabaseRecordStore } from './useUpdateDatabaseRecordStore';
import { send_component_save_request, send_request } from '../../utils/Request';
import './UpdateDatabaseRecordStyle.css';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import ItemCell from './ItemCell';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import Icon from '@mui/material/Icon';
import checkKey from '../../utils/CheckKey';
import ChipInput from '../ChipInput/ChipInput';
import { showLoadingScreen } from '../../utils/showLoadingScreen';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';

const UpdateDatabaseRecordTemplate = (props) => {
  const [databaseList, setDatabaseList] = useState([]);
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getUpdateDatabaseRecordStore(props.component_id), [props.component_id]);
  const data = store(s => s.data);
  const { targetDatabaseId, updateMethod, key, id, columns } = data;

  useEffect(() => {
    if (props.component_id !== undefined) {
      setLoadingScreen(true);

      // Skip primary component data fetch if already loaded (prefilled from keyed store)
      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        setLoadingScreen(false);
        props.setChildTemplateLoaded(true);
      }

      // Secondary fetch: database list (always needed)
      send_request(`database/get_databases`, '', {}, 'GET').then(response => {
        if (response.data) {
          store.getState().setDatabaseList(response.data);
          setDatabaseList(response.data);

          if (store.getState().loaded) return;

          send_request(
            `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
            '',
            {}
          ).then(response => {
            if (
              response.data &&
              response.data.components &&
              response.data.components[0].componentData
            ) {
              props.onComponentNameChanged(response.data.components[0].componentData.name);
              props.onComponentDescriptionChanged(response.data.components[0].description);
              props.onLastModifiedChanged(
                response.data.components[0].componentData.lastModified
              );

              let queryData = response.data.components[0].componentData.data;
              if (!Array.isArray(queryData)) {
                queryData = [];
              }
              store.getState().setUpdateDatabaseRecordTemplate(
                response.data.components[0]
              );
              store.setState({ loaded: true });
              setLoadingScreen(false);
            } else {
              store.getState().setUpdateDatabaseRecordTemplate(
                response.data.components[0]
              );
              store.setState({ loaded: true });
              setLoadingScreen(false);
            }

            props.setChildTemplateLoaded(true);
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, isNotPlaceholder);
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style, isNotPlaceholder) => {
    const storeState = store.getState();
    const phs = [...storeState.placeholders, { label, text, style }];
    storeState.setPlaceholders(phs);
    if (isNotPlaceholder !== true) {
      text = '${' + text + '}';
    }
    if (storeState.focus == 'key') {
      storeState.setId(text);
    }
  };

  const updateTargetDatabase = (targetDbId) => {
    if (targetDbId != null) {
      send_request(`database/get_columns/${targetDbId}`, '', {}, 'GET').then(response => {
        if (response.data) {
          if (response.data.length > 0) {
            addColumns(response.data);
          } else {
            store.getState().clearData();
          }
        }
        setIsLoading(false);
      });
    } else {
      store.getState().clearData();
      setIsLoading(false);
    }
  };

  const addColumns = (colData, existingCellValues) => {
    let newColumns = [];
    const storeData = store.getState().data;

    colData.forEach(cell => {
      const col = storeData.columns.filter(
        c => c.columnName === cell.name || c.columnName === cell.columnName
      );

      let value = col.length > 0 ? col[0].content : cell.content;

      if (!value && existingCellValues) {
        for (let i = 0; i < existingCellValues.length; i++) {
          if (cell.columnId !== existingCellValues[i].columnId) continue;
          value = existingCellValues[i].content;
          break;
        }
      }

      const checked = col.length > 0 ? col[0].checked : true;

      newColumns.push({
        columnId: cell.columnId,
        columnName: cell.columnName ? cell.columnName : cell.name,
        type: cell.type,
        content: value,
        checked: checked,
        orderNumber: cell.orderNumber,
      });
    });

    store.getState().clearData();
    newColumns.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));
    store.getState().setData(newColumns);
  };

  const handleKeyChange = e => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setPlaceholderKey(placeholderValue);

    const storeData = store.getState().data;
    const value = checkKey(placeholderValue, storeData);

    setCheckKeyError(value[0]);
    setErrorMessage(value[1]);
  };

  const { component, availablePlaceholders } = props;

  let selectAll = true;
  columns.forEach(cell => {
    if (!cell.checked) {
      selectAll = false;
    }
  });

  if (!loadingScreen) {
    return (
      <Grid container xs={12}>
        <Grid item xs={12}>
          <CustomPaper className="query-item-paper defaultText">
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid
                item
                xs={12}
                sm={12}
                md={2}
                lg={2}
                xl={2}
                className="item-label-update-record text-normal"
              >
                Placeholder{' '}
              </Grid>
              <Grid item xs={12} sm={12} md={10} lg={10} xl={10} className="key-bulk-grid">
                <Grid item xs={12} sm={6} md={4} lg={5} xl={5}>
                  <W86PlaceholderTextField
                    value={key}
                    error={checkKeyError ? true : false}
                    helperText={checkKeyError ? errorMessage : null}
                    placeholderText="Enter placeholder for update table"
                    handleChange={handleKeyChange}
                  />
                </Grid>
              </Grid>
            </Grid>

            <SelectProject
              databaseList={databaseList}
              updateTargetProject={updateTargetDatabase}
              setTargetDatabaseId={targetDbId => {
                store.getState().setTargetDatabaseId(targetDbId);
                useProjectStore.getState().setTargetDatabaseId(targetDbId);
              }}
              targetDatabaseId={targetDatabaseId}
              component={component}
              isLoading={isLoading}
              setIsLoading={val => {
                setIsLoading(val);
              }}
              updateDatabaseList={dbList => {
                setDatabaseList(dbList);
                store.getState().setDatabaseList(dbList);
              }}
            />
            <Grid container xs={12} style={{ margin: '16px 0' }}>
              <Grid item direction="column" xs={12} style={{ marginBottom: 8 }}>
                Update method
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={12} md={5} lg={5} xl={5} style={{ paddingRight: 16 }}>
                    <Select
                      style={{
                        width: '100%',
                        backgroundColor: '#F8F8F8',
                        borderRadius: '4px',
                        height: '36px',
                        color: '#868686',
                        marginRight: '24px',
                        marginBottom: 8,
                        verticalAlign: 'top',
                      }}
                      name="selectType"
                      disableUnderline
                      variant="standard"
                      labelId="demo-simple-select-outlined-label"
                      className="selectStandard"
                      displayEmpty
                      onChange={event =>
                        store.getState().setUpdateMethod(event.target.value)
                      }
                      value={updateMethod}
                    >
                      <MenuItem value={'update_by_record_id'}> Update by Record ID </MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container xs={12}>
              <Grid item direction="column" xs={12}>
                {updateMethod === 'update_by_record_id' ? 'Record ID ' : 'Session ID '}
                to update
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} style={{ paddingRight: 16 }}>
                    <ChipInput
                      onPlaceholderSelected={props.onPlaceholderSelected}
                      style={{ marginTop: '8px' }}
                      inputValue={id ? [id] : []}
                      placeholders={availablePlaceholders}
                      placeholder="Type something or insert a placeholder"
                      fullWidth
                      onBeforeAdd={chip => {
                        if (chip.trim() != '') {
                          store.getState().setId(chip.trim());
                          return true;
                        }
                      }}
                      onDelete={() => store.getState().deleteId()}
                      onFocus={() => store.getState().setFocus('key', 'key')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {columns.length > 0 ? (
              <Grid container>
                <FormControl component="fieldset" className="form-control-update">
                  <Grid item xs={12} className="query-item-content">
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          inputProps={{ 'aria-label': 'secondary checkbox' }}
                        />
                      }
                      label={
                        <p className="query-item-content select-all">Select columns to update</p>
                      }
                      onChange={() => {
                        store.getState().selectAll();
                      }}
                      checked={selectAll ? true : false}
                    />
                  </Grid>
                  <FormGroup>
                    {columns.map((column, index) => (
                      <Grid style={{ display: 'flex' }} key={index}>
                        <FormControlLabel
                          label={''}
                          control={
                            <Checkbox
                              color="primary"
                              inputProps={{
                                'aria-label': 'secondary checkbox',
                              }}
                            />
                          }
                          spellCheck={true}
                          className="select-cell"
                          checked={column.checked ? true : false}
                          onChange={event => {
                            store.getState().setChecked(event.target.checked, index);
                          }}
                          style={{ alignItems: 'center' }}
                        />
                        <ItemCell
                          column={column}
                          columns={columns}
                          availablePlaceholders={availablePlaceholders}
                          index={index}
                          style={!column.checked ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                        />
                      </Grid>
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            ) : (
              ''
            )}
          </CustomPaper>
        </Grid>
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default UpdateDatabaseRecordTemplate;
